import { useEffect, useState } from 'react'
import { FundPicker, useFunds } from '../components/FundPicker'
import { Fragment } from 'react'
import {
  api,
  type AwaitingDate,
  type AwaitingMapping,
  type LayoutProfile,
  type Portfolio,
  type RefusedSubmission,
  type Sender,
} from '../api'
import { ShapeMapper } from '../components/ShapeMapper'
import { EffectiveDateBinding } from '../components/EffectiveDateBinding'
import { RowMenu } from '../components/RowMenu'
import { DeskCell, DeskHeader, useDesks } from '../desks'
import { sortRows, useSort } from '../components/Grid'

/**
 * The mailbox queues — TECHNICAL_DESIGN.md §8.2, §8.3, §8.5, §8.6.
 *
 * Three lists that look alike and are not, and keeping them apart is the screen's
 * whole job:
 *
 * > Rejected submissions produce a queue entry for a compliance officer with the
 * > reason and the document attached. **Nothing is silently dropped, and nothing
 * > ambiguous is silently resolved.**
 *
 * **Refusals are about content and belong to a person who can act on them.** Each
 * entry leads with what refused it and the sentence that caused it — *"'8%'
 * appears near 'we are considering'"* — because the useful thing to do next is
 * call the client, and *"rejected"* on its own is a reason the reviewer has to
 * reconstruct.
 *
 * **An unmapped shape is not a client problem**, so it is a separate list with a
 * separate owner: one confirmation owed by whoever manages layout profiles (§8.3),
 * grouped by shape because ten submissions in one unknown layout are one piece of
 * work. Showing ten rows would read as a backlog somebody is failing to clear.
 *
 * **The allowlist shows revoked entries.** A list of live addresses cannot answer
 * *"who was allowed to send this when it arrived"*, which is the question asked
 * about an instruction accepted last year.
 *
 * **There is no accept button anywhere on this screen**, and the absence is the
 * design. §8.6: sender authorisation decides whether a submission is *considered*;
 * a named approver decides whether it *takes effect*, on the approvals screen,
 * against the proposal. A shortcut from here would put an outside party in direct
 * control of a binding limit.
 */
const KINDS: Record<string, string> = {
  dmarc_failed: 'Sender could not be established',
  sender_not_authorised: 'Sender not authorised',
  domain_mismatch: 'Arrived from an unexpected domain',
  not_literally_present: 'Value not found in the document',
  multiple_candidates: 'The field read two ways',
  required_field_absent: 'A required field is missing',
  hedged_language: 'Hedged or conditional wording',
  self_contradictory: 'The document contradicts itself',
  amendment_intent_needed: 'Meets a limit already in force',
  store_refused: 'Uses a term this firm has not defined',
  format_not_readable: 'Nothing configured can read this file',
}

export function Submissions({
  section,
  canAuthorise = false,
  canMap = false,
}: {
  /** Chosen from the main menu — see `App`. */
  section: 'refusals' | 'shapes' | 'senders'
  /** §10.1 puts the allowlist with the connector's administrator, and the
   *  separation is load-bearing: the person who configures the mailbox is not
   *  the person who decides what to do about what it received. */
  canAuthorise?: boolean
  /** §10.1 puts layout profiles with the appliance administrator: mapping a
   *  client document's shape is a parsing concern, deliberately separate from
   *  dictionary vocabulary and from deciding what the values mean. */
  canMap?: boolean
}) {
  const [refusals, setRefusals] = useState<RefusedSubmission[]>([])
  const [shapes, setShapes] = useState<AwaitingMapping[]>([])
  /** Confirmed shapes. The queue on its own answers *what is stuck* and never
   *  *what already works* — and an administrator asked to trust a one-mapping
   *  economy wants to see the mappings it has accumulated. */
  const [profiles, setProfiles] = useState<LayoutProfile[]>([])
  const [shapeTab, setShapeTab] = useState<'pending' | 'dated' | 'done'>('pending')
  const [undated, setUndated] = useState<AwaitingDate[]>([])
  // The date typed against each waiting document, by its id.
  const [dates, setDates] = useState<Record<string, string>>({})
  const [readNote, setReadNote] = useState<string | null>(null)
  const [mapping, setMapping] = useState<string | null>(null)
  const [rebinding, setRebinding] = useState<string | null>(null)
  // A mapped layout whose row meanings are open for editing (decision 71).
  const [remapping, setRemapping] = useState<string | null>(null)
  const [senders, setSenders] = useState<Sender[]>([])
  /** The senders' Desk column, for somebody across the whole firm (decision 91). */
  const [senderDesk, setSenderDesk] = useState('')
  const senderSort = useSort()
  const directory = useDesks()
  const { funds } = useFunds()
  const [form, setForm] = useState({ email: '' })
  /* Many funds, one address, one act. The authorisation stays *per fund* —
     §8.6's model is deliberate and a consultant acting for one mandate is not
     thereby acting for the rest — but a client's operations desk sends one file
     covering forty funds, and typing the same address forty times is a data
     entry problem being solved with a security argument. */
  const [picking, setPicking] = useState('')
  const [forFunds, setForFunds] = useState<Portfolio[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setError(null)
    try {
      const [refused, unmapped, allowed, mapped, waitingDates] = await Promise.all([
        api.refusedSubmissions(),
        api.awaitingMapping(),
        api.senders(),
        api.layoutProfiles(),
        api.awaitingDate(),
      ])
      setRefusals(refused)
      setShapes(unmapped)
      setUndated(waitingDates)
      setSenders(allowed)
      setProfiles(mapped)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function readWithDate(doc: AwaitingDate) {
    const on = dates[doc.id]
    if (!on) return
    setBusy(true)
    setError(null)
    setReadNote(null)
    try {
      const outcome = await api.replayShape(doc.fingerprint, on, doc.id)
      setReadNote(
        outcome.proposals > 0
          ? `${doc.filename ?? 'The document'} read as taking effect on ${on}: ` +
              `${outcome.proposals} proposal(s) now waiting on Approvals.`
          : `${doc.filename ?? 'The document'} read. Anything that could not be read is on Refused items with its reason.`,
      )
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /* Derived, never typed. `authorise_sender` refuses any pinned domain that is
     not the address's own — so the field could hold exactly one value and
     refused every other, while its label ("must arrive from") invited the mail
     server's hostname, which is the thing an administrator has just finished
     configuring elsewhere. A required input with one correct answer computable
     from another input is not an input. */
  const pinned = form.email.includes('@')
    ? (form.email.trim().toLowerCase().split('@').pop() ?? '')
    : ''

  const ready = form.email.trim().includes('@') && forFunds.length > 0

  function addFund() {
    const fund = funds.find((f) => f.id === picking)
    if (!fund || forFunds.some((f) => f.id === fund.id)) return
    setForFunds([...forFunds, fund])
    setPicking('')
  }

  async function authorise() {
    if (!ready) return
    setBusy(true)
    setError(null)
    let done = 0
    const refused: string[] = []

    // One authorisation per fund, written one at a time and never aborting.
    // They are separately revocable and separately audited, which is the point:
    // this is one act of *typing*, not one authorisation covering several
    // mandates. There is deliberately no wildcard — an address authorised for
    // "every fund" would silently acquire the funds created next year.
    for (const fund of forFunds) {
      try {
        await api.authoriseSender(form.email.trim(), pinned, fund.id)
        done += 1
      } catch (e) {
        refused.push(`${fund.code}: ${e instanceof Error ? e.message : String(e)}`)
      }
    }

    if (refused.length) {
      setError(`${done} authorised, ${refused.length} refused — ${refused.join('; ')}`)
    }
    setForm({ email: '' })
    setForFunds([])
    try {
      setSenders(await api.senders())
    } catch {
      // The list is a convenience; a stale one is not worth reporting over the
      // outcome of the change just made.
    }
    setBusy(false)
  }

  /** Revoked rows stay, with the date. An allowlist showing only live entries
   *  cannot say who was allowed to send an instruction that arrived last year
   *  (§8.6) — so this closes an authorisation rather than deleting one. */
  async function revoke(id: string) {
    setBusy(true)
    setError(null)
    try {
      await api.revokeSender(id)
      setSenders(await api.senders())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function close(entry: RefusedSubmission) {
    // Required, not optional, and asked for rather than defaulted: it is what an
    // auditor reads about an instruction the firm did not action (§8.5).
    const note = window.prompt('What was done about this instruction?')
    if (!note?.trim()) return

    setBusy(true)
    setError(null)
    try {
      await api.handleSubmission(entry.id, note.trim())
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="submissions">
      <header className="page-head">
        <h2>Guideline mailbox</h2>
        <p className="note">
          What arrived, what was refused and why, and who is allowed to send.
          Nothing here becomes a live limit: an instruction that passes every
          check is staged as a proposal for a named approver.
        </p>
      </header>


      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <section hidden={section !== 'refusals'}>
        <h3>Refused instructions ({refusals.length})</h3>
        <p className="note wide">
          Received and not acted on. Nothing here has changed a limit — closing an
          entry records that somebody dealt with it, and the instruction itself is
          entered in the portal or resent by the client.
        </p>
        {refusals.length === 0 ? (
          <p className="done" data-testid="no-refusals">
            Nothing waiting.
          </p>
        ) : (
          <table className="grid has-menus">
            <thead>
              <tr>
                <th>Refused because</th>
                <th>From</th>
                <th>Document</th>
                <th>Arrived</th>
                <th>Read</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {refusals.map((r) => (
                <tr key={r.id} data-testid="refusal">
                  <td>
                    <b>{KINDS[r.refusal_kind] ?? r.refusal_kind}</b>
                    {/* The sentence that caused it, verbatim. The kind sorts the
                        queue; the reason is what somebody acts on. */}
                    <div className="note" data-testid="refusal-reason">
                      {r.refusal_reason}
                    </div>
                  </td>
                  <td>
                    {r.sender_address}
                    {r.portfolio_code && <div className="note">{r.portfolio_code}</div>}
                  </td>
                  <td>
                    {/* The client's own file. Working a refusal means reading
                        what they actually sent — the reason says why this
                        appliance would not take it, not what it says — and the
                        document was on the uploads volume with no way to reach
                        it from the one screen that needs it. */}
                    {r.stored_uri ? (
                      <a
                        href={`/api/submissions/${r.id}/document`}
                        data-testid="refused-document"
                      >
                        {r.filename ?? 'the attachment'}
                      </a>
                    ) : (
                      <>
                        {r.filename ?? '—'}
                        <div className="clause">
                          not kept — this message was refused before any
                          attachment was read
                        </div>
                      </>
                    )}
                    {r.subject && <div className="note">{r.subject}</div>}
                  </td>
                  {/* Two timestamps, because they are two facts and a document
                      that waited a fortnight for its layout to be mapped shows
                      the gap here and nowhere else. Equal on a live poll. */}
                  <td className="clause">
                    {new Date(r.received_at).toLocaleString()}
                  </td>
                  <td className="clause">
                    {new Date(r.read_at).toLocaleString()}
                    {new Date(r.read_at).getTime() -
                      new Date(r.received_at).getTime() >
                      60_000 && (
                      <div className="note" data-testid="waited">
                        waited{' '}
                        {Math.round(
                          (new Date(r.read_at).getTime() -
                            new Date(r.received_at).getTime()) /
                            86_400_000,
                        )}{' '}
                        day(s) to be read
                      </div>
                    )}
                  </td>
                  <td className="row-actions">
                    <RowMenu
                      label={`Actions for ${r.filename ?? r.sender_address}`}
                      disabled={busy}
                      testId={`refusal-menu-${r.id}`}
                      items={[
                        {
                          label: 'Open what the client sent',
                          onSelect: () =>
                            window.open(
                              `/api/submissions/${r.id}/document`,
                              '_blank',
                              'noopener',
                            ),
                          disabled: !r.stored_uri,
                          why: 'This message was refused before any attachment was read, so nothing was kept.',
                        },
                        {
                          label: 'Record what was done',
                          onSelect: () => void close(r),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section hidden={section !== 'shapes'}>
        <h3>Document layouts</h3>
        <p className="note wide">
          A layout is the <em>shape</em> of a client's file — where the table
          starts and what each row means. One confirmation per shape, after which
          every document arriving in it is read deterministically, with no model
          involved. The waiting count is how much is held up by one piece of
          work, not a backlog.
        </p>
        <p className="note wide">
          <strong>Documents waiting</strong> is what has arrived and not been
          read. <strong>Layouts mapped</strong> is the shapes you have confirmed.
          A shape appears in both between the two steps: mapping it says how to
          read the file, and reading the documents already waiting is a separate
          press — so that a mapping can be right while the backlog still wants
          looking at.
        </p>

        <div className="tabstrip" role="tablist" aria-label="Which layouts">
          {(
            [
              // Named for what each list *holds*, because they hold different
              // kinds of thing: documents on the left, layouts on the right. As
              // "Waiting for a mapping" and "Mapped" they read as two states of
              // one thing, so a shape that is mapped while its documents are
              // still unread — the ordinary state between the two buttons —
              // appeared in both and looked like a fault.
              ['pending', 'Documents waiting', shapes.length],
              ['dated', 'Waiting for a date', undated.length],
              ['done', 'Layouts mapped', profiles.length],
            ] as const
          ).map(([option, label, count]) => (
            <button
              key={option}
              role="tab"
              aria-selected={shapeTab === option}
              onClick={() => {
                setShapeTab(option)
                setMapping(null)
              }}
            >
              {label}
              <span className="count">{count}</span>
            </button>
          ))}
        </div>

        {shapeTab === 'dated' ? (
          <>
            {readNote && <p className="done">{readNote}</p>}
            {undated.length === 0 ? (
              <p className="done" data-testid="no-undated">
                No document is waiting for its effective date.
              </p>
            ) : (
              <table className="grid" data-testid="undated-list">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Layout</th>
                    <th>From</th>
                    <th>Arrived</th>
                    <th>Takes effect on</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {undated.map((d) => (
                    <tr key={d.id} data-testid="undated">
                      <td>
                        <a href={`/api/submissions/${d.id}/document`} title="Open the document as sent">
                          {d.filename ?? 'document'}
                        </a>
                        {d.subject && <span className="clause">{d.subject}</span>}
                      </td>
                      <td>{d.layout ?? '—'}</td>
                      <td>{d.sender_address}</td>
                      <td>{new Date(d.received_at).toLocaleDateString()}</td>
                      <td>
                        <input
                          type="date"
                          value={dates[d.id] ?? ''}
                          disabled={!canMap}
                          onChange={(e) => setDates({ ...dates, [d.id]: e.target.value })}
                          aria-label={`Takes effect on, for ${d.filename ?? 'this document'}`}
                        />
                      </td>
                      <td className="row-actions">
                        {canMap ? (
                          <button disabled={busy || !dates[d.id]} onClick={() => void readWithDate(d)}>
                            Read it
                          </button>
                        ) : (
                          <span className="clause">An administrator gives the date.</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        ) : shapeTab === 'pending' ? (
          shapes.length === 0 ? (
            <p className="done" data-testid="no-shapes">
              Every shape that has arrived is mapped. Documents in a known shape
              are read without asking anyone.
            </p>
          ) : (
            <table className="grid has-menus">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>From</th>
                  <th>Waiting</th>
                  <th>First seen</th>
                  <th>Last seen</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shapes.map((s) => (
                  <Fragment key={s.fingerprint}>
                    <tr data-testid="shape">
                      <td>
                        {s.example_filename ?? s.fingerprint}
                        {/* A shape whose mapping is confirmed but whose backlog
                            has not been read stays in this queue, and looked
                            exactly like one nobody had touched — so Confirm was
                            pressed again and again on a mapping that had saved
                            the first time. */}
                        {profiles.some((p) => p.fingerprint === s.fingerprint) && (
                          <>
                            {' '}
                            <span className="clause" data-testid="mapped-not-read">
                              — mapped, not yet read
                            </span>
                          </>
                        )}
                      </td>
                      <td>{s.example_sender}</td>
                      <td className="limit">{s.submissions} submission(s) waiting</td>
                      <td>{new Date(s.first_seen).toLocaleDateString()}</td>
                      <td>{new Date(s.last_seen).toLocaleDateString()}</td>
                      <td className="row-actions">
                        {canMap ? (
                          <button
                            aria-expanded={mapping === s.fingerprint}
                            onClick={() =>
                              setMapping(mapping === s.fingerprint ? null : s.fingerprint)
                            }
                          >
                            {mapping === s.fingerprint ? 'Close' : 'Map it'}
                          </button>
                        ) : (
                          <span className="clause">
                            An administrator maps the shape.
                          </span>
                        )}
                      </td>
                    </tr>
                    {mapping === s.fingerprint && (
                      <tr className="history-row">
                        <td colSpan={6}>
                          <ShapeMapper shape={s} onDone={() => void load()} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )
        ) : profiles.length === 0 ? (
          <p className="empty" data-testid="no-profiles">
            No layout has been confirmed yet. The first document in a new shape
            asks once; after that it never asks again.
          </p>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Layout</th>
                <th>Sheet</th>
                <th>Table starts at</th>
                <th>Rows mapped</th>
                <th>Takes effect</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <Fragment key={p.id}>
                  <tr data-testid="layout-profile">
                    <td>{p.name}</td>
                    <td className="clause">{p.sheet_name ?? '—'}</td>
                    <td>row {p.header_row + 1}</td>
                    <td className="limit">{p.mapped_rows}</td>
                    <td>
                      {p.effective_date_source === 'arrival' ? (
                        'on the day it arrives'
                      ) : p.effective_date_source === 'document' ? (
                        'on the date given for each document'
                      ) : p.effective_date_cell ? (
                        <>from cell <strong>{p.effective_date_cell}</strong></>
                      ) : (
                        // Not a cosmetic gap. This layout refuses every
                        // document that arrives in it, and until now the
                        // Mapped tab was where that looked like success.
                        <span className="warn" data-testid="no-date-binding">
                          not set — documents in this shape are refused
                        </span>
                      )}
                    </td>
                    <td className="row-actions">
                      {canMap ? (
                        <RowMenu
                          label={`Actions for ${p.name}`}
                          testId="layout-menu"
                          items={[
                            {
                              label: 'Row meanings',
                              disabled: !p.document_submission_id,
                              why: 'No document in this layout has arrived by mail to map against',
                              onSelect: () => {
                                setRebinding(null)
                                setRemapping(remapping === p.id ? null : p.id)
                              },
                            },
                            {
                              label: 'Effective date',
                              onSelect: () => {
                                setRemapping(null)
                                setRebinding(rebinding === p.id ? null : p.id)
                              },
                            },
                          ]}
                        />
                      ) : (
                        <span className="clause">An administrator sets this.</span>
                      )}
                    </td>
                  </tr>
                  {remapping === p.id && p.document_submission_id && (
                    <tr className="history-row">
                      <td colSpan={6}>
                        <ShapeMapper
                          shape={{
                            fingerprint: p.fingerprint,
                            submissions: 0,
                            first_seen: '',
                            last_seen: '',
                            example_filename: p.name,
                            example_submission_id: p.document_submission_id,
                            example_sender: '',
                          }}
                          onDone={() => void load()}
                        />
                      </td>
                    </tr>
                  )}
                  {rebinding === p.id && (
                    <tr className="history-row">
                      <td colSpan={6}>
                        <EffectiveDateBinding
                          profile={p}
                          onDone={() => {
                            setRebinding(null)
                            void load()
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section hidden={section !== 'senders'}>
        <h3>Who may submit</h3>
        <p className="note">
          Per fund, pinned to the domain the address must arrive from. Being on
          this list decides whether an instruction is <i>considered</i>; an
          approver still decides whether it takes effect.
        </p>
        <p className="note">
          One address can be authorised for as many funds as it sends for —
          a client's operations desk often files one sheet covering the lot. Each
          fund is a separate authorisation, separately revocable, because an
          address acting for one mandate is not thereby acting for the rest. There
          is no <i>every fund</i>: it would silently pick up the funds created
          next year.
        </p>
        {/* The list was read-only, so a screen whose whole job is *who may send*
            could tell you nobody could and offered no way to change it. The
            routes have existed since §8.6 shipped. */}
        {canAuthorise && (
          <form
            className="row-form"
            data-testid="authorise-sender"
            onSubmit={(e) => {
              e.preventDefault()
              void authorise()
            }}
          >
            <label>
              Address
              <input
                type="email"
                value={form.email}
                required
                placeholder="ops@client.test"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              {/* The pinned domain is not shown at all. It was a field, and
                  every plausible answer to it was wrong — the tempting one is
                  the sending server's hostname, which is precisely what DMARC
                  alignment is not. Read-only it still read as a box to fill.
                  There is nothing to decide here: an address pins its own
                  domain by construction, so the form sends it and says
                  nothing. If a message ever arrives from elsewhere, the
                  refusal on Refused items explains it in full — which is where
                  the fact is actually needed. */}
            </label>
            <FundPicker
              label="For which funds"
              value={picking}
              onChange={setPicking}
              funds={funds.filter((f) => !forFunds.some((c) => c.id === f.id))}
              placeholder="Choose a fund…"
              testId="sender-fund"
            />
            <button
              type="button"
              className="ghost"
              disabled={!picking}
              data-testid="add-sender-fund"
              onClick={addFund}
            >
              + Add
            </button>
            <button type="submit" disabled={busy || !ready}>
              {busy
                ? 'Authorising…'
                : `Authorise for ${forFunds.length || 'no'} fund(s)`}
            </button>
          </form>
        )}

        {forFunds.length > 0 && (
          <ul className="chosen" data-testid="sender-funds">
            {forFunds.map((fund) => (
              <li key={fund.id}>
                <code>{fund.code}</code>
                <button
                  className="link"
                  aria-label={`Remove ${fund.code}`}
                  onClick={() => setForFunds(forFunds.filter((f) => f.id !== fund.id))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {senders.length === 0 ? (
          <p className="note" data-testid="no-senders">
            No addresses are authorised yet. Until one is added, every message sent
            to the mailbox is listed under <strong>Refused items</strong> with its
            reason.
            {!canAuthorise && ' An administrator adds an address here.'}
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <DeskHeader
                  sort={senderSort.sort}
                  onSort={senderSort.onSort}
                  value={senderDesk}
                  onChange={setSenderDesk}
                />
                <th>Address</th>
                <th>Must arrive from</th>
                <th>Portfolio</th>
                <th>Status</th>
                {canAuthorise && <th />}
              </tr>
            </thead>
            <tbody>
              {sortRows(
                senders.filter((s) => !senderDesk || directory.fund(s.portfolio_code) === senderDesk),
                senderSort.sort,
                { desk: (s) => directory.fund(s.portfolio_code) ?? '' },
              ).map((s) => (
                <tr key={s.id} data-testid="sender">
                  <DeskCell fund={s.portfolio_code} />
                  <td>{s.email}</td>
                  <td>{s.expected_domain}</td>
                  <td>{s.portfolio_code}</td>
                  <td>
                    {/* Revoked rows stay, with the date. An allowlist showing only
                        live entries cannot say who was allowed to send an
                        instruction that arrived last year (§8.6). */}
                    {s.revoked_at
                      ? `Revoked ${new Date(s.revoked_at).toLocaleDateString()}`
                      : 'Live'}
                  </td>
                  {canAuthorise && (
                    <td className="row-actions">
                      {!s.revoked_at && (
                        <button
                          className="danger"
                          disabled={busy}
                          onClick={() => void revoke(s.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}
